import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../styles.module.css';
import { Pencil } from '../../../assets/svgs/icons';
import { Message } from './Message';
import { StepsList } from './StepsList';
import { OptionsButtons } from './OptionsButtons';
import { AttachImageButton } from '../../../shared/campaign/AttachImageButton';
import { AttachReferenceArt } from '../../../components/AttachReferenceArt';
import { AnimatedPlaceholder } from './AnimatedPlaceholder';
import ArtLimitProgress from '../../../components/ArtLimitProgress';
import { notifyErrorCustom } from '../../../utils/notify';
import { DownloadZipCard } from './DownloadZipCard';
import { AISuggestionBanner } from './AISuggestionBanner';

const seenPostQuantityTypingIds = new Set();

const MemoizedDynamicContent = memo(({ msg, renderDynamicComponent, queueSteps }) => {
  const content = useMemo(() => {
    if (!renderDynamicComponent) {
      return msg.children || null;
    }
    return renderDynamicComponent(msg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msg, renderDynamicComponent, queueSteps]);

  return content ? <div>{content}</div> : null;
});
MemoizedDynamicContent.displayName = 'MemoizedDynamicContent';

export const ChatPage = ({
  sendMessage,
  messages,
  message,
  setMessage,
  isFullChatLayout,
  currentQuestion,
  onSelectOption,
  onRespond,
  onSkip,
  selectedItems = {},
  onPasteImage,
  showQueueProgress = false,
  queueSteps = [],
  artSteps = [],
  onOpenStepsImageSelector,
  onReferenceArtSelect,
  renderDynamicComponent,
  campaignName,
  onCampaignNameChange,
  selectedArt = null,
  isEditingArt = false,
  isLoadingBatchEditArt = false,
  onEditArtRequest,
  artAttachedImages = [],
  onArtAttachImages,
  onOpenProductImageSelector,
  isCampaignStarted,
  artesCriadas = 0,
  limiteArtes = 0,
  disableInputFromPreview = false,
  artLimitFallback = null,
  isArtsApproved = false,
  canUseAI = false,
  onClickZipCard,
  isZipDownloading = false,
  showAISuggestionBanner = false,
  onGenerateSuggestion,
  isGeneratingSuggestion = false,
  hasAvailableAISuggestions = true,
  isSubmittingMessage = false,
}) => {
  const POST_QUANTITY_COMPONENT_DELAY_MS = 650;

  const { t } = useTranslation();

  const displayCampaignName = campaignName || t('chatsocket.default_campaign_name');

  const [attachArtOpen, setAttachArtOpen] = useState(false)
  const [postQuantityVisibilityByMessage, setPostQuantityVisibilityByMessage] = useState({});

  const [editingName, setEditingName] = useState(displayCampaignName)
  const [isEditingCampaignName, setIsEditingCampaignName] = useState(false)
  const [typedMessageIds, setTypedMessageIds] = useState({});

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const postQuantityRevealTimeoutsRef = useRef(new Map());

  const scrollToBottom = useCallback((behavior = 'auto') => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTo({
            top: messagesContainerRef.current.scrollHeight,
            behavior,
          });
        }

        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({
            behavior,
            block: 'end'
          });
        }
      });
    });
  }, []);

  const handleMessageRendered = useCallback(() => {
    scrollToBottom('auto');
  }, [scrollToBottom]);

  useEffect(() => {
    scrollToBottom();
  }, [
    messages,
    currentQuestion?.options?.length,
    currentQuestion?.component,
    currentQuestion?.stepName,
    isLoadingBatchEditArt,
  ]);

  useEffect(() => {
    scrollToBottom('auto');
  }, [isFullChatLayout]);

  useEffect(() => {
    scrollToBottom();
  }, [postQuantityVisibilityByMessage]);

  useEffect(() => {
    return () => {
      postQuantityRevealTimeoutsRef.current.forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
      postQuantityRevealTimeoutsRef.current.clear();
    };
  }, []);

  useEffect(() => {
    if (!Array.isArray(messages) || messages.length === 0) {
      seenPostQuantityTypingIds.clear();
    }
  }, [messages]);

  const getMessageRenderKey = (msg, index) => {
    const stepOrder = msg?.stepOrder ?? 'step';
    const messageOrder = msg?._origMessageOrder ?? msg?.messageOrder ?? 'order';
    const originalIndex = msg?._originalIndex ?? index;
    const component = msg?.component ?? 'none';
    const backendFingerprint =
      typeof msg?.backend === 'string' && msg.backend.length > 0
        ? msg.backend
        : 'no-backend';

    return `${component}-${stepOrder}-${messageOrder}-${originalIndex}-${backendFingerprint}`;
  };

  const schedulePostQuantityReveal = (messageKey) => {
    if (!messageKey || postQuantityRevealTimeoutsRef.current.has(messageKey)) {
      return;
    }

    const timeoutId = setTimeout(() => {
      setPostQuantityVisibilityByMessage((previous) => ({
        ...previous,
        [messageKey]: true,
      }));
      postQuantityRevealTimeoutsRef.current.delete(messageKey);
    }, POST_QUANTITY_COMPONENT_DELAY_MS);

    postQuantityRevealTimeoutsRef.current.set(messageKey, timeoutId);
  };

  const getImageUrl = (image) => {
    if (!image) return null;
    if (image.fileDataBase64) return image.fileDataBase64;
    if (image.image) return image.image;
    if (image.base64) return `data:image/png;base64,${image.base64}`;
    if (image.url) return image.url;
    if (typeof image === 'string') return image;
    return null;
  };

  const hasImages = Object.values(selectedItems).some(
    config => config.items && config.items.length > 0
  );

  const hasAnyAttachedImage = hasImages || artAttachedImages.length > 0;

  const isArtEditBlocked = artLimitFallback?.mode === 'freeze';
  const isComposerBusy =
    isSubmittingMessage || isEditingArt || isLoadingBatchEditArt;
  const isArtEditMode = !!selectedArt && !isEditingArt && canUseAI && !isArtEditBlocked;
  const isBatchEdit = currentQuestion?.stepName === 'batch_edit_art';
  const isRejecting = currentQuestion?.stepName === 'reject_strategy' || currentQuestion?.stepName === 'reject_art';
  const showBar = isArtEditMode || isBatchEdit || isRejecting;
  const useCompactInput = isRejecting && !isArtEditMode;
  const showOptions = currentQuestion?.options?.length > 0 && !hasAnyAttachedImage && !isLoadingBatchEditArt;

  useEffect(() => {
    if (isComposerBusy) {
      setAttachArtOpen(false);
    }
  }, [isComposerBusy]);

  const renderedOptions = useMemo(() => {
    if (!Array.isArray(currentQuestion?.options)) {
      return [];
    }

    let options = currentQuestion.options;

    if (!artLimitFallback?.mode) {
      options = currentQuestion.options;
    } else {
      const isFreezeFallback = artLimitFallback.mode === 'freeze';
      const disabledReason =
        isFreezeFallback
          ? t('chatsocket.chat.disabled_freeze')
          : t('chatsocket.chat.disabled_batch_pause');

      options = currentQuestion.options.map((option) => {
        const isBlockedCampaignRestart =
          isFreezeFallback &&
          (option.name === t('chatsocket.buttons.new_campaign') ||
            option.type === 'MOCK_NEW_CAMPAIGN');

        if (option.type !== 'edit' && option.type !== 'reject' && !isBlockedCampaignRestart) {
          return option;
        }

        return {
          ...option,
          disabled: true,
          disabledReason,
        };
      });
    }

    if (currentQuestion?.optionsDisabled) {
      return options.map((option) => ({
        ...option,
        disabled: true,
      }));
    }

    return options;
  }, [artLimitFallback?.mode, currentQuestion?.options, currentQuestion?.optionsDisabled, t]);

  const placeholderTexts = useMemo(() => {
    if (isArtEditBlocked) {
      return [t('chatsocket.chat.placeholder_blocked')];
    }

    const isArtEditMode = !!selectedArt && !isEditingArt && canUseAI;

    if (isArtEditMode) {
      return [t('chatsocket.chat.placeholder_art_edit')];
    }

    if (currentQuestion?.component && !hasImages) {
      return [t('chatsocket.chat.placeholder_options')];
    }
    if (currentQuestion?.stepName === 'edit_strategy') {
      return [t('chatsocket.chat.placeholder_strategy_edit')];
    }
    if (currentQuestion?.stepName === 'reject_strategy' || currentQuestion?.stepName === 'batch_edit_art') {
      return [t('chatsocket.chat.placeholder_strategy_reject')];
    }

    const placeholders = currentQuestion?.placeholders;
    if (placeholders && placeholders.length > 0) {
      return [...placeholders];
    }

    return [t('chatsocket.chat.placeholder_default')];
  }, [isArtEditBlocked, selectedArt, isEditingArt, currentQuestion?.component, currentQuestion?.stepName, currentQuestion?.placeholders, hasImages]);

  const sortedMessages = useMemo(() => {
    const arr = Array.isArray(messages)
      ? messages.map((msg, index) => ({ ...msg, _originalIndex: index }))
      : [];

    arr.sort((a, b) => {
      const maRaw = a?.messageOrder;
      const mbRaw = b?.messageOrder;

      // Negative messageOrders (parameterization step: -1, -1.1, -1.2)
      // should preserve the original array order from the backend.
      const bothNegative =
        typeof maRaw === 'number' && maRaw < 0 &&
        typeof mbRaw === 'number' && mbRaw < 0;
      if (bothNegative) {
        return a._originalIndex - b._originalIndex;
      }

      const ma = Number(maRaw ?? 0);
      const mb = Number(mbRaw ?? 0);
      if (ma !== mb) return ma - mb;

      return a._originalIndex - b._originalIndex;
    });

    return arr;
  }, [messages]);

  const getMessageTypingId = useCallback((msg, index) => (
    [
      msg?.stepOrder ?? '',
      msg?.messageOrder ?? '',
      msg?.component ?? '',
      msg?.backend ?? '',
      msg?._originalIndex ?? index,
    ].join('|')
  ), []);

  const markMessageAsTyped = useCallback((messageTypingId) => {
    if (!messageTypingId) return;
    setTypedMessageIds((prev) => {
      if (prev[messageTypingId]) {
        return prev;
      }
      return {
        ...prev,
        [messageTypingId]: true,
      };
    });
  }, []);

  useEffect(() => {
    sortedMessages.forEach((msg, index) => {
      if (msg?.component !== 'PostQuantitySelector' || !msg?.backend || msg?.disableTypingAnimation) {
        return;
      }

      const messageTypingId = getMessageTypingId(msg, index);
      if (!messageTypingId || seenPostQuantityTypingIds.has(messageTypingId)) {
        return;
      }

      seenPostQuantityTypingIds.add(messageTypingId);
    });
  }, [getMessageTypingId, sortedMessages]);

  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const imageFiles = Array.from(items).filter(
      item => item.type.startsWith('image/')
    );

    if (imageFiles.length === 0) return;

    e.preventDefault();

    const processedImages = (await Promise.all(
      imageFiles.map((item) => {
        return new Promise((resolve) => {
          const file = item.getAsFile();

          if (file.size > 10 * 1024 * 1024) {
            notifyErrorCustom(
              t('chatsocket.errors.file_too_large_message'),
              t('chatsocket.errors.file_too_large_title'),
              8000,
              {
                width: '580px',
                right: 320
              }
            );
            resolve(null);
            return;
          }

          const reader = new FileReader();

          reader.onload = () => {
            resolve({
              fileDataBase64: reader.result,
              name: file.name || `imagem-colada-${Date.now()}.png`,
              size: file.size,
              type: file.type,
              source: 'gallery',
              id: `img-${Date.now()}-${Math.random()}`,
            });
          };

          reader.readAsDataURL(file);
        });
      })
    )).filter(Boolean);

    if (onPasteImage && processedImages.length > 0) {
      onPasteImage(processedImages);
    }
  };

  const hasVisibleMessages = messages.some((m) => m.frontend || m.backend);

  return (
    <div className={`${styles.chatWrapper} ${!isCampaignStarted && !showAISuggestionBanner ? styles.chatWrapper_small : ''} ${isFullChatLayout ? styles.fullChat : ''} ${showAISuggestionBanner ? styles.chatWrapper_withBanner : ''} ${showOptions ? styles.chatWrapper_withOptions : ''}`}>
      {isFullChatLayout && (
        <div className={styles.ChatHeader}>
          <div className={styles.headerStatus}>
            <div className={styles.statusIndicator}>
              <div className={styles.statusIndicator_bolinha}></div>
            </div>
            <span className={styles.statusText}>{t('chatsocket.chat.status_online')}</span>
          </div>
          <div className={styles.headerSeparator}></div>
          <div className={styles.campaignNameContainer}>
            {isEditingCampaignName ? (
              <input
                type="text"
                className={styles.campaignNameInput}
                value={editingName}
                onChange={(e) => setEditingName(e.target.value)}
                onBlur={() => {
                  setIsEditingCampaignName(false);
                  if (editingName.trim() && editingName !== campaignName) {
                    onCampaignNameChange?.(editingName.trim());
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setIsEditingCampaignName(false);
                    if (editingName.trim() && editingName !== campaignName) {
                      onCampaignNameChange?.(editingName.trim());
                    }
                  }
                }}
                autoFocus
              />
            ) : (
              <span
                className={styles.campaignNameText}
                onClick={() => {
                  setEditingName(campaignName);
                  setIsEditingCampaignName(true);
                }}
              >
                {displayCampaignName}
              </span>
            )}
            <button
              className={styles.editButton}
              onClick={() => {
                setEditingName(campaignName);
                setIsEditingCampaignName(true);
              }}
              type="button"
            >
              <Pencil color="#9002CD" size="16" />
            </button>
          </div>
        </div>
      )}
      <div
        ref={messagesContainerRef}
        className={`${styles.chat_messages} ${!isCampaignStarted ? styles.chat_messages_small : ''} ${isFullChatLayout ? styles.fullChat : ''} ${showOptions ? styles.chat_messages_withOptions : ''} ${!hasVisibleMessages && !isFullChatLayout ? styles.chat_messages_empty : ''}`}
      >
        {sortedMessages.map((msg, i) => {
            if (!msg.frontend && !msg.backend) return null;

            const isUserMessage = !!msg.frontend;
            const messageKey = getMessageRenderKey(msg, i);

            const lastBackendMessageIndex = sortedMessages
              .map((m, idx) => (!m.frontend && m.backend ? idx : -1))
              .filter((idx) => idx !== -1)
              .pop();

            const isLastBackendMessage = !isUserMessage && i === lastBackendMessageIndex;
            const messageTypingId = getMessageTypingId(msg, i);
            const isPostQuantityMessage = msg.component === 'PostQuantitySelector';
            const shouldAnimatePostQuantityMessage =
              isPostQuantityMessage &&
              !!msg.backend &&
              !msg.disableTypingAnimation &&
              !typedMessageIds[messageTypingId] &&
              !seenPostQuantityTypingIds.has(messageTypingId);
            const shouldDelayPostQuantityComponent =
              shouldAnimatePostQuantityMessage;
            const isPostQuantityComponentVisible =
              !shouldDelayPostQuantityComponent ||
              !!postQuantityVisibilityByMessage[messageKey];
            const shouldDelayArtQueueProgress =
              msg.component === 'ArtQueueProgress' &&
              !isUserMessage &&
              isLastBackendMessage &&
              !msg.disableTypingAnimation;
            const canRenderArtQueueProgress =
              !shouldDelayArtQueueProgress || !!typedMessageIds[messageTypingId];

            return (
              <Message
                isUserMessage={isUserMessage}
                msg={msg}
                onSelectOption={onSelectOption}
                key={i}
                onRenderMessage={handleMessageRendered}
                userImageUrl={'https://img.freepik.com/vetores-gratis/ilustracao-do-jovem-sorridente_1308-174669.jpg?semt=ais_hybrid&w=740&q=80'}
                userName={'usuario 1'}
                currentQuestion={currentQuestion}
                isLastBackendMessage={isLastBackendMessage}
                forceTypingAnimation={shouldAnimatePostQuantityMessage}
                onRespond={() => onRespond && onRespond(msg.messageOrder)}
                onSkip={onSkip}
                onTypingComplete={() => {
                  markMessageAsTyped(messageTypingId);
                  handleMessageRendered();
                  if (shouldDelayPostQuantityComponent) {
                    schedulePostQuantityReveal(messageKey);
                  }

                }}
              >
                {(() => {
                  if (isPostQuantityMessage && !isPostQuantityComponentVisible) {
                    return null;
                  }

                  return (
                    <MemoizedDynamicContent
                      msg={msg}
                      renderDynamicComponent={renderDynamicComponent}
                      queueSteps={queueSteps}
                    />
                  );
                })()}

                {msg.isQueueProgressMessage && showQueueProgress && (
                  <div>
                    <StepsList
                      title={t('chatsocket.chat.creating_strategy')}
                      steps={queueSteps}
                    />
                  </div>
                )}
                {msg.component === 'ArtQueueProgress' && canRenderArtQueueProgress && (
                  <div>
                    <StepsList
                      title={t('chatsocket.chat.creating_arts')}
                      steps={artSteps}
                    />
                  </div>
                )}

              </Message>
            );
          })}
          <div
            className={`${styles.chatBottomSpacer} ${showOptions ? styles.chatBottomSpacer_withOptions : ''} ${showBar && showOptions ? styles.chatBottomSpacer_withOptionsCompact : ''}`}
          />
        <div ref={messagesEndRef} />
      </div>

      <form className={styles.inputForm} onSubmit={(e) => {
        e.preventDefault();
        if (isArtEditBlocked || isComposerBusy) {
          return;
        }
        const textInputSteps = ['edit_strategy', 'reject_strategy', 'batch_edit_art'];
        const isTextInputStep = textInputSteps.includes(currentQuestion?.stepName);
        const isArtEditMode = !!selectedArt && !isEditingArt;
        const hasOptions = !!currentQuestion?.options?.length;
        const canSendWithCurrentQuestion = !hasOptions || currentQuestion?.isMockOption || isTextInputStep || hasAnyAttachedImage;
        // If we're in batch edit mode, prefer sending the message to the
        // batch handler even if a single art is currently selected.
        if (isArtEditMode && (message.trim() || hasAnyAttachedImage) && currentQuestion?.stepName !== 'batch_edit_art') {
          onEditArtRequest?.(message);
          return;
        }
        if ((message.trim() || hasAnyAttachedImage) && canSendWithCurrentQuestion) {
          sendMessage(message);
        }
      }}>
        {isRejecting && (
          <ArtLimitProgress
            artesCriadas={artesCriadas}
            limiteArtes={limiteArtes}
            layout="chat"
          />
        )}
        <div className={`${styles.inputContainer} ${useCompactInput ? styles.inputContainer_compact : styles.inputContainer_small} ${showOptions ? styles.inputContainer_withOptions : ''}`}>
          {(isArtEditMode || isBatchEdit) && (
            <div className={styles.progressOverlay}>
              <ArtLimitProgress
                artesCriadas={artesCriadas}
                limiteArtes={limiteArtes}
                layout="chat"
              />
            </div>
          )}
          {hasAnyAttachedImage && (
            <div className={styles.imagesPreview}>
              {Object.entries(selectedItems).map(([componentName, config]) =>
                config.items?.map((item, index) => {
                  const imageUrl = componentName === 'VisualStyleSelector'
                    ? item?.imageUrl
                    : getImageUrl(item);

                  if (!imageUrl) return null;

                  const handleRemove = () => {
                    if (config.onRemove) {
                      if (componentName === 'VisualStyleSelector') {
                        config.onRemove();
                      } else {
                        config.onRemove(index);
                      }
                    }
                  };

                  const isReferenceArt = componentName === 'ReferenceArt';

                  return (
                    <div key={`${componentName}-${index}`} className={isReferenceArt ? styles.imagePreviewItemOrange : styles.imagePreviewItem}>
                      <img src={imageUrl} alt={t('chatsocket.chat.preview')} className={styles.previewImage} />
                      <button
                        type="button"
                        className={styles.removeImageButton}
                        onClick={handleRemove}
                        title={t('chatsocket.buttons.remove_image')}
                      >
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                      {config.onEdit && (
                        <div
                          className={styles.editOverlay}
                          onClick={config.onEdit}
                          title={t('chatsocket.chat.edit_overlay')}
                        />
                      )}
                    </div>
                  );
                })
              )}
              {artAttachedImages.map((img, index) => {
                const imageUrl = getImageUrl(img);
                if (!imageUrl) return null;
                return (
                  <div key={`art-attach-${index}`} className={styles.imagePreviewItem}>
                    <img src={imageUrl} alt={t('chatsocket.chat.preview')} className={styles.previewImage} />
                    <button
                      type="button"
                      className={styles.removeImageButton}
                      onClick={() => {
                        const updated = artAttachedImages.filter((_, i) => i !== index);
                        onArtAttachImages?.(updated);
                      }}
                      title={t('chatsocket.buttons.remove_image')}
                    >
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          {(() => {
            const textInputSteps = ['edit_strategy', 'reject_strategy', 'batch_edit_art'];
            const isTextInputStep = textInputSteps.includes(currentQuestion?.stepName);
            const isArtEditMode = !!selectedArt && !isEditingArt && !isArtEditBlocked;
            const isDisabled = isArtEditBlocked || isArtsApproved || disableInputFromPreview || isComposerBusy || !isArtEditMode && !isTextInputStep && (
              (currentQuestion?.options?.length > 0 && !currentQuestion?.isMockOption) ||
              !!currentQuestion?.component
            );

            const showPlaceholderOverlay = !message.trim() && (isDisabled || !hasImages);

            return (
              <>
                {showPlaceholderOverlay && (
                  <div className={styles.placeholderOverlay}>
                    {isEditingArt ? (
                      <span>{t('chatsocket.chat.wait_edit')}</span>
                    ) : (
                      <AnimatedPlaceholder
                        texts={placeholderTexts}
                        typingSpeed={40}
                      />
                    )}
                  </div>
                )}
                <textarea
                  className={`${styles.input} ${!isCampaignStarted ? styles.input_small : ''} ${isDisabled ? styles.disabled : ''} ${currentQuestion?.stepName === 'reject_strategy' || currentQuestion?.stepName === 'reject_art' ? styles.input_small : ''}`}
                  value={message}
                  onChange={(e) => {
                    if (isDisabled) {
                      return;
                    }
                    setMessage(e.target.value);
                  }}
                  onPaste={handlePaste}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      e.stopPropagation();
                      if (isArtEditBlocked || isComposerBusy) {
                        return;
                      }
                      if (isArtEditMode && (message.trim() || hasAnyAttachedImage) && currentQuestion?.stepName !== 'batch_edit_art') {
                        onEditArtRequest?.(message);
                        return;
                      }
                      const hasOptions = !!currentQuestion?.options?.length;
                      const canSendWithCurrentQuestion = !hasOptions || currentQuestion?.isMockOption || isTextInputStep || hasAnyAttachedImage;
                      if ((message.trim() || hasAnyAttachedImage) && canSendWithCurrentQuestion) {
                        sendMessage(message);
                      }
                    }
                  }}
                  placeholder=""
                  rows={2}
                  disabled={isDisabled}
                />
              </>
            );
          })()}

          {showOptions && (
            <OptionsButtons
              options={renderedOptions}
              onSelectOption={onSelectOption}
              showBar={showBar}
            />
          )}

          <div className={styles.inputActions}>
            <div className={styles.inputActionsDivider}>
              <div style={{ position: 'relative' }}>
                <div className={styles.attach_wrapper}>
                  <AttachImageButton
                    visible={
                      !isArtsApproved && (
                        currentQuestion?.component === 'ProductImageSelector' ||
                        currentQuestion?.component === 'VisualStyleSelector' ||
                        selectedArt
                      )
                    }
                    onClick={() => setAttachArtOpen(!attachArtOpen)}
                    disabled={isComposerBusy}
                  />
                </div>

                {currentQuestion?.component === 'DownloadZipCard' && (
                  <DownloadZipCard onClick={onClickZipCard} disabled={isZipDownloading} />
                )}
                {!isArtsApproved && (currentQuestion?.component === 'ProductImageSelector' || currentQuestion?.component === 'VisualStyleSelector' || selectedArt) && (
                  <AttachReferenceArt
                    open={attachArtOpen}
                    setOpen={setAttachArtOpen}
                    onImageSelect={onReferenceArtSelect}
                    verticalLayout={true}
                    hideExpandButton={true}
                    menuPosition="top"
                    menuClassName={styles.chatAttachMenu}
                    disabled={isComposerBusy}
                    options={[
                      // {
                      //   id: 'reference',
                      //   label: 'Logotipo',
                      //description: 'Opcional. Envie o logo da sua marca para ser inserido nas artes.',
                      //disabled: currentQuestion?.component === 'ProductImageSelector',
                      //   onSelect: (imageData) => onReferenceArtSelect?.(imageData),
                      // },
                      {
                        id: 'type',
                        label: t('chatsocket.buttons.images'),
                        //disabled: currentQuestion?.component === 'VisualStyleSelector',
                        openModalOnly: true,
                        onSelect: () => onOpenStepsImageSelector?.('Principal'),
                      },
                      {
                        id: 'reference',
                        label: t('chatsocket.buttons.logotipo'),
                        //description: 'Opcional. Envie o logo da sua marca para ser inserido nas artes.',
                        //disabled: currentQuestion?.component === 'ProductImageSelector',
                        onSelect: (imageData) => onReferenceArtSelect?.(imageData),
                      },
                    ]}
                    acceptedFormats="image/*"
                    maxFileSize={10 * 1024 * 1024}
                    showPreview={false}
                  />
                )}
              </div>
            </div>
            <button type="submit" className={`${styles.sendButton} ${isComposerBusy ? styles.sendButtonDisabled : ''}`} disabled={isComposerBusy}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20.2346 5.68597C20.6666 4.49097 19.5086 3.33297 18.3136 3.76597L3.70855 9.04797C2.50955 9.48197 2.36455 11.118 3.46755 11.757L8.12955 14.456L12.2926 10.293C12.4812 10.1108 12.7338 10.01 12.996 10.0123C13.2582 10.0146 13.509 10.1197 13.6944 10.3051C13.8798 10.4906 13.9849 10.7414 13.9872 11.0036C13.9895 11.2658 13.8887 11.5184 13.7066 11.707L9.54355 15.87L12.2436 20.532C12.8816 21.635 14.5176 21.489 14.9516 20.291L20.2346 5.68597Z" fill="white" />
              </svg>
            </button>
          </div>
        </div>
      </form>
      {showAISuggestionBanner && (
        <AISuggestionBanner
          onGenerateSuggestion={onGenerateSuggestion}
          isLoading={isGeneratingSuggestion}
          disabled={!hasAvailableAISuggestions}
          tooltipText={t('plans.ai_suggestion_limit_reached')}
        />
      )}
    </div>
  );
};
